import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authAPI as authService } from "@/services/api";

import type { User } from "@/types/auth";

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

type AuthProviderProps = {
  children: ReactNode;
};

// Create context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider component
export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem("token")
  );

  // Query to get current user
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["auth-user"],
    queryFn: async () => {
      if (!token) return null;
      try {
        const userData = await authService.getCurrentUser();
        return userData;
      } catch (error) {
        console.error("Error fetching user data:", error);
        throw error;
      }
    },
    enabled: !!token, // Only run if we have a token
    retry: false, // Don't retry on error
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      console.log("Attempting login with:", credentials.email);
      try {
        const response = await authService.login(
          credentials.email,
          credentials.password
        );
        console.log("Login successful, received token");
        return response;
      } catch (error) {
        console.error("Login error in mutation:", error);
        throw error; // Make sure to re-throw the error
      }
    },
    onSuccess: (data) => {
      console.log("Login mutation success, setting token");
      setToken(data.access_token);
      localStorage.setItem("token", data.access_token);

      // Invalidate and refetch user data after login
      queryClient.invalidateQueries({ queryKey: ["auth-user"] });

      navigate("/");
    },
  });

  // Handle token cleanup on error during session, not during login
  useEffect(() => {
    if (error && token) {
      console.warn("Session error, clearing token:", error);
      localStorage.removeItem("token");
      setToken(null);
      navigate("/login");
    }
  }, [error, navigate, token]);

  // Login handler - explicit promise handling
  const login = async (email: string, password: string) => {
    console.log("Login function called");
    try {
      const result = await loginMutation.mutateAsync({ email, password });
      console.log("Login mutation completed successfully");
      return result;
    } catch (error) {
      console.error("Login failed, error caught in login function:", error);
      throw error; // Re-throw to let component handle it
    }
  };

  // Logout handler
  const logout = () => {
    console.log("Logout called");
    localStorage.removeItem("token");
    setToken(null);
    queryClient.clear(); // Clear all queries from cache
    navigate("/login");
  };

  // Context value
  const value = {
    user,
    isAuthenticated: !!token && !!user,
    isLoading: isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook for using auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
